/**
 * Min / max over an array without spreading it into a call.
 *
 * `Math.min(...values)` passes every element as an argument, and the engine
 * overflows the call stack somewhere past ~100k of them (less inside a deep
 * React render). A 20 Hz logger never gets near that, but a RaceChrono export
 * writes a row at every sensor's update (GPS + accel + gyro + magnetometer,
 * ~300 rows/s), so one long lap is a 100k+ point trace and the analysis page
 * crashed with "Maximum call stack size exceeded". Use these for anything
 * sized by trace points; the spread form is fine for a handful of values.
 *
 * `null`/`undefined` entries are skipped (`Math.min(null, 4)` is 0, which is
 * how a missing sample used to drag an axis to zero); NaN never compares true
 * so it falls out on its own.
 */
export function arrayMin(values) {
  let min = Infinity;
  for (const v of values) if (v != null && v < min) min = v;
  return min;
}

export function arrayMax(values) {
  let max = -Infinity;
  for (const v of values) if (v != null && v > max) max = v;
  return max;
}

/** `[min, max]` in one pass, or `null` when nothing finite was seen. */
export function arrayExtent(values) {
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (v == null) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return min <= max ? [min, max] : null;
}
